import React, { useRef, useEffect, useState, useCallback } from "react";
import MapboxMap, {
  Marker,
  Source,
  Layer,
  NavigationControl,
  Popup,
} from "react-map-gl/mapbox";
// mapbox-gl + mapbox-gl.css are loaded from CDN in public/index.html
// (avoids webpack/terser bundling, which previously corrupted runtime).
import { MapPin, AlertTriangle } from "lucide-react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const HAS_TOKEN =
  MAPBOX_TOKEN && !MAPBOX_TOKEN.includes("PLACEHOLDER") && MAPBOX_TOKEN.startsWith("pk.");

// Cache directions across renders (keyed by lng,lat,lng,lat)
const directionsCache = new Map();

async function fetchDirections(s_lng, s_lat, e_lng, e_lat) {
  const key = `${s_lng},${s_lat};${e_lng},${e_lat}`;
  if (directionsCache.has(key)) return directionsCache.get(key);
  try {
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${s_lng},${s_lat};${e_lng},${e_lat}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
    const resp = await fetch(url);
    const data = await resp.json();
    const geom = data?.routes?.[0]?.geometry;
    if (geom) {
      directionsCache.set(key, geom);
      return geom;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

const heatmapLayer = {
  id: "heatmap",
  type: "heatmap",
  source: "heatmap",
  maxzoom: 14,
  paint: {
    "heatmap-weight": ["interpolate", ["linear"], ["get", "weight"], 0, 0, 5, 1],
    "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 1, 14, 3],
    "heatmap-color": [
      "interpolate",
      ["linear"],
      ["heatmap-density"],
      0,
      "rgba(33,102,172,0)",
      0.2,
      "rgb(103,169,207)",
      0.4,
      "rgb(209,229,240)",
      0.6,
      "rgb(253,219,199)",
      0.8,
      "rgb(239,138,98)",
      1,
      "rgb(178,24,43)",
    ],
    "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 6, 14, 30],
    "heatmap-opacity": 0.7,
  },
};

export default function ProjectMap({
  projects = [],
  segments = [],
  heatmap = [],
  height = 480,
  selectable = false,
  onPick,
  initialPick = null,
}) {
  const mapRef = useRef(null);
  const [popup, setPopup] = useState(null);
  const [pickPoints, setPickPoints] = useState(initialPick || []);
  const [routeGeoms, setRouteGeoms] = useState({});

  // Sync external pick changes (e.g. when parent updates from manual inputs)
  useEffect(() => {
    if (!selectable) return;
    setPickPoints(initialPick || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialPick), selectable]);

  // Fetch real road directions for each project (snap to road network)
  useEffect(() => {
    if (!HAS_TOKEN) return;
    let cancelled = false;
    (async () => {
      const next = { ...routeGeoms };
      let changed = false;
      for (const p of projects) {
        if (
          p.start_lng != null &&
          p.start_lat != null &&
          p.end_lng != null &&
          p.end_lat != null &&
          !next[p.id]
        ) {
          const geom = await fetchDirections(
            p.start_lng,
            p.start_lat,
            p.end_lng,
            p.end_lat
          );
          if (geom) {
            next[p.id] = geom;
            changed = true;
          }
        }
      }
      if (!cancelled && changed) setRouteGeoms(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // Azerbaijan bounds — restricts panning and minimum zoom to whole country view
  // SW corner (lon, lat) → NE corner (lon, lat)
  const AZ_BOUNDS = [[44.0, 38.3], [51.0, 42.0]];

  const initialView = {
    longitude: 47.5,
    latitude: 40.2,
    zoom: 6.5,
  };

  // Build GeoJSON for projects: prefer real road geometry, fallback to straight line
  const projectLines = {
    type: "FeatureCollection",
    features: projects
      .filter(
        (p) =>
          p.start_lng != null &&
          p.start_lat != null &&
          p.end_lng != null &&
          p.end_lat != null
      )
      .map((p) => ({
        type: "Feature",
        properties: { id: p.id, name: p.name, snapped: !!routeGeoms[p.id] },
        geometry: routeGeoms[p.id] || {
          type: "LineString",
          coordinates: [
            [p.start_lng, p.start_lat],
            [p.end_lng, p.end_lat],
          ],
        },
      })),
  };

  const heatmapGeo = {
    type: "FeatureCollection",
    features: heatmap.map((h) => ({
      type: "Feature",
      properties: { weight: h.weight },
      geometry: { type: "Point", coordinates: [h.lng, h.lat] },
    })),
  };

  const handleClick = useCallback(
    (e) => {
      if (!selectable) return;
      const { lng, lat } = e.lngLat;
      const next = [...pickPoints, { lng, lat }].slice(-2);
      setPickPoints(next);
      onPick?.(next);
    },
    [selectable, pickPoints, onPick]
  );

  if (!HAS_TOKEN) {
    return (
      <div
        className="bg-slate-50 border border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-center p-6"
        style={{ height }}
        data-testid="map-no-token"
      >
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <h3 className="font-bold text-lg mb-1">Mapbox token tələb olunur</h3>
        <p className="text-sm text-slate-600 max-w-md">
          Xəritəni göstərmək üçün <code className="mono bg-slate-200 px-1 rounded">REACT_APP_MAPBOX_TOKEN</code>{" "}
          dəyişənini <code className="mono bg-slate-200 px-1 rounded">/app/frontend/.env</code> faylına əlavə edin (mapbox.com → Account → Tokens).
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg overflow-hidden border border-slate-200"
      data-testid="project-map"
      style={{ height }}
    >
      <MapboxMap
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={initialView}
        maxBounds={AZ_BOUNDS}
        minZoom={6}
        maxZoom={16}
        mapStyle="mapbox://styles/mapbox/light-v11"
        onClick={handleClick}
        cursor={selectable ? "crosshair" : "grab"}
      >
        <NavigationControl position="top-right" />

        {projectLines.features.length > 0 && (
          <Source id="project-lines" type="geojson" data={projectLines}>
            {/* white casing for visibility on any map style */}
            <Layer
              id="lines-casing"
              type="line"
              paint={{
                "line-color": "#ffffff",
                "line-width": 7,
                "line-opacity": 0.9,
              }}
              layout={{ "line-cap": "round", "line-join": "round" }}
            />
            <Layer
              id="lines"
              type="line"
              paint={{
                "line-color": "#004D99",
                "line-width": 4,
                "line-opacity": 0.9,
              }}
              layout={{ "line-cap": "round", "line-join": "round" }}
            />
          </Source>
        )}

        {heatmap.length > 0 && (
          <Source id="heatmap" type="geojson" data={heatmapGeo}>
            <Layer {...heatmapLayer} />
          </Source>
        )}

        {projects.map(
          (p) =>
            p.start_lng != null && (
              <Marker
                key={`p-${p.id}`}
                longitude={p.start_lng}
                latitude={p.start_lat}
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setPopup({ type: "project", data: p });
                }}
              >
                <div className="bg-[#004D99] text-white rounded-full p-2 shadow-lg cursor-pointer hover:scale-110 transition-transform">
                  <MapPin className="w-4 h-4" />
                </div>
              </Marker>
            )
        )}

        {segments.map((s) => {
          if (s.start_lng == null) return null;
          const color = s.not_ok_count > s.ok_count ? "#EF4444" : "#10B981";
          return (
            <Marker
              key={`s-${s.id}`}
              longitude={s.start_lng}
              latitude={s.start_lat}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setPopup({ type: "segment", data: s });
              }}
            >
              <div
                className="rounded-full w-3 h-3 border-2 border-white shadow cursor-pointer"
                style={{ backgroundColor: color }}
              />
            </Marker>
          );
        })}

        {pickPoints.map((pt, i) => {
          if (pt?.lng == null || pt?.lat == null) return null;
          return (
            <Marker key={`pick-${i}`} longitude={pt.lng} latitude={pt.lat}>
              <div className={`text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-lg ${i === 0 ? "bg-[#FF5722]" : "bg-[#004D99]"}`}>
                {i === 0 ? "A" : "B"}
              </div>
            </Marker>
          );
        })}

        {popup && (
          <Popup
            longitude={
              popup.type === "project"
                ? popup.data.start_lng
                : popup.data.start_lng
            }
            latitude={
              popup.type === "project" ? popup.data.start_lat : popup.data.start_lat
            }
            onClose={() => setPopup(null)}
            closeButton
            closeOnClick={false}
            anchor="bottom"
          >
            {popup.type === "project" ? (
              <div className="text-sm">
                <div className="font-bold">{popup.data.name}</div>
                <div className="mono text-xs text-slate-600">
                  {popup.data.length_km} km
                </div>
              </div>
            ) : (
              <div className="text-sm">
                <div className="font-bold">
                  {popup.data.start_km}–{popup.data.end_km} km
                </div>
                <div className="mono text-xs text-slate-600">
                  OK: {popup.data.ok_count} · NOT OK: {popup.data.not_ok_count}
                </div>
                <div className="mono text-xs text-slate-600">
                  İcra: {popup.data.completion_pct}%
                </div>
              </div>
            )}
          </Popup>
        )}
      </MapboxMap>
    </div>
  );
}
